// app/(owner)/index.tsx
import { useOwnerAuth } from "@/components/owner/OwnerAuthProvider";
import { db } from "@/lib/firebase";
import { resolveOwnerEntryPath } from "@/lib/onboardingResume";
import { router } from "expo-router";
import {
  collection,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import React, { useEffect } from "react";
import { ActivityIndicator, Text, View } from "react-native";

type BizStatus =
  | "draft"
  | "pending"
  | "approved"
  | "rejected"
  | "changes_requested";

type BizDoc = {
  status?: BizStatus;
  isDeleted?: boolean;
  updatedAt?: unknown;
  createdAt?: unknown;
};

function toMillis(value: unknown): number {
  if (!value) return 0;

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (
    typeof value === "object" &&
    "toMillis" in value &&
    typeof (value as { toMillis?: unknown }).toMillis === "function"
  ) {
    return (value as { toMillis: () => number }).toMillis();
  }

  return 0;
}

export default function OwnerEntry() {
  const { authInitialized, user, ownerReady, ownerError } = useOwnerAuth();

  useEffect(() => {
    let cancelled = false;

    if (!authInitialized) return;
    if (!user) {
      router.replace("/(owner)/onboarding/signin" as any);
      return;
    }
    if (!ownerReady) {
      if (ownerError) router.replace("/(owner)/dashboard" as any);
      return;
    }

    void (async () => {
      try {
        const businessesQuery = query(
          collection(db, "businesses"),
          where("ownerId", "==", user.uid),
          limit(50)
        );

        const snap = await getDocs(businessesQuery);

        if (cancelled) return;

        const businesses = snap.docs
          .map((docSnap) => ({
            id: docSnap.id,
            ...(docSnap.data() as BizDoc),
          }))
          .filter((business) => business.isDeleted !== true)
          .sort((a, b) => {
            const aMs = toMillis(a.updatedAt) || toMillis(a.createdAt);
            const bMs = toMillis(b.updatedAt) || toMillis(b.createdAt);

            return bMs - aMs;
          });

        router.replace(resolveOwnerEntryPath(businesses) as any);
      } catch {
        console.warn("Owner entry route recovery failed.");

        router.replace("/(owner)/dashboard" as any);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authInitialized, user, ownerReady, ownerError]);

  return (
    <View
      style={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#FFFFFF",
        padding: 20,
      }}
    >
      <ActivityIndicator />

      <Text
        style={{
          marginTop: 12,
          fontWeight: "900",
          color: "#111827",
        }}
      >
        Loading owner workspace...
      </Text>
    </View>
  );
}
